import { baseApi } from "./baseApi";

// ── Types ──────────────────────────────────────────────────────────────────

export type Track = "junior" | "senior";

export type EnrollmentStatus =
  | "pending"
  | "contacted"
  | "enrolled"
  | "declined";

export interface ParentSnapshot {
  parentName: string;
  email: string;
  phone: string;
}

export interface EnrollmentChild {
  _id?: string;
  childName: string;
  childAge: number;
  track: Track;
  interests: string[];
  parent?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Enrollment {
  _id: string;
  parent: string | ParentRecord;
  children: Array<string | ChildRecord>;
  parentSnapshot: ParentSnapshot;
  childrenSnapshot: EnrollmentChild[];
  message: string;
  status: EnrollmentStatus;
  createdAt: string;
  updatedAt: string;
}

export interface ParentRecord {
  _id: string;
  parentName: string;
  email: string;
  phone: string;
  linkedUser?: string | null;
  customerStage?: "lead" | "contacted" | "customer";
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ChildRecord {
  _id: string;
  parent: string;
  childName: string;
  childAge: number;
  track: Track;
  interests: string[];
  linkedUser?: string | null;
  studentStage?: "prospect" | "enrolled" | "active" | "inactive";
  createdAt?: string;
  updatedAt?: string;
}

// ── Request / Response shapes ──────────────────────────────────────────────

export interface SubmitEnrollmentChildRequest {
  childName: string;
  childAge: number | string;
  interests: string[];
}

export interface SubmitEnrollmentRequest {
  parentName: string;
  email: string;
  phone: string;
  children: SubmitEnrollmentChildRequest[];
  message?: string;
}

export interface UpdateEnrollmentStatusRequest {
  id: string;
  status: EnrollmentStatus;
}

export interface GetEnrollmentsRequest {
  status?: EnrollmentStatus;
  page?: number;
  limit?: number;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: Pagination;
}

// ── Injected API ───────────────────────────────────────────────────────────

export const enrollmentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Submit a new enrollment (public — no auth required)
     * POST /api/enrollments
     */
    submitEnrollment: builder.mutation<
      ApiResponse<Enrollment>,
      SubmitEnrollmentRequest
    >({
      query: (body) => ({
        url: "/enrollments",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Enrollment", id: "LIST" }],
    }),

    /**
     * Get all enrollments with optional filters (admin)
     * GET /api/enrollments?status=pending&page=1&limit=20
     */
    getEnrollments: builder.query<
      PaginatedResponse<Enrollment>,
      GetEnrollmentsRequest
    >({
      query: ({ status, page = 1, limit = 20 } = {}) => {
        const params = new URLSearchParams();
        if (status) params.set("status", status);
        params.set("page", String(page));
        params.set("limit", String(limit));
        return `/enrollments?${params.toString()}`;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ _id }) => ({
                type: "Enrollment" as const,
                id: _id,
              })),
              { type: "Enrollment", id: "LIST" },
            ]
          : [{ type: "Enrollment", id: "LIST" }],
    }),

    /**
     * Get a single enrollment by ID (admin)
     * GET /api/enrollments/:id
     */
    getEnrollmentById: builder.query<ApiResponse<Enrollment>, string>({
      query: (id) => `/enrollments/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Enrollment", id }],
    }),

    /**
     * Update enrollment status (admin)
     * PATCH /api/enrollments/:id/status
     */
    updateEnrollmentStatus: builder.mutation<
      ApiResponse<Enrollment>,
      UpdateEnrollmentStatusRequest
    >({
      query: ({ id, status }) => ({
        url: `/enrollments/${id}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Enrollment", id },
        { type: "Enrollment", id: "LIST" },
      ],
    }),

    /**
     * Delete an enrollment (admin)
     * DELETE /api/enrollments/:id
     */
    deleteEnrollment: builder.mutation<ApiResponse<null>, string>({
      query: (id) => ({
        url: `/enrollments/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "Enrollment", id },
        { type: "Enrollment", id: "LIST" },
      ],
    }),
  }),

  overrideExisting: false,
});

// ── Named exports for use in components ───────────────────────────────────

export const {
  useSubmitEnrollmentMutation,
  useGetEnrollmentsQuery,
  useGetEnrollmentByIdQuery,
  useUpdateEnrollmentStatusMutation,
  useDeleteEnrollmentMutation,
} = enrollmentApi;